/**
 * Logger Utility
 * Centralized logging for the application
 * In production, this can be extended to send logs to external services
 */

const LOG_LEVELS = {
  ERROR: "error",
  WARN: "warn",
  INFO: "info",
  DEBUG: "debug",
};

/**
 * Determines if application is in development mode
 */
const isDevelopment = process.env.NODE_ENV === "development";

/**
 * Formats log message with timestamp and context
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {Object} context - Additional context data
 * @returns {string} Formatted log message
 */
const formatLogMessage = (level, message, context) => {
  const timestamp = new Date().toISOString();
  const contextStr = context ? ` | Context: ${JSON.stringify(context)}` : "";
  return `[${timestamp}] [${level.toUpperCase()}] ${message}${contextStr}`;
};

/**
 * Logs error message
 * @param {string} message - Error message
 * @param {Object} context - Additional error context
 */
const error = (message, context = {}) => {
  const formattedMessage = formatLogMessage(LOG_LEVELS.ERROR, message, context);

  if (isDevelopment) {
    console.error(formattedMessage);
  }

  // In production, send to external logging service
  // Example: sendToLoggingService(LOG_LEVELS.ERROR, message, context);
};

/**
 * Logs warning message
 * @param {string} message - Warning message
 * @param {Object} context - Additional warning context
 */
const warn = (message, context = {}) => {
  const formattedMessage = formatLogMessage(LOG_LEVELS.WARN, message, context);

  if (isDevelopment) {
    console.warn(formattedMessage);
  }
};

/**
 * Logs info message
 * @param {string} message - Info message
 * @param {Object} context - Additional info context
 */
const info = (message, context = {}) => {
  const formattedMessage = formatLogMessage(LOG_LEVELS.INFO, message, context);

  if (isDevelopment) {
    console.info(formattedMessage);
  }
};

/**
 * Logs debug message (only in development)
 * @param {string} message - Debug message
 * @param {Object} context - Additional debug context
 */
const debug = (message, context = {}) => {
  if (!isDevelopment) {
    return;
  }

  const formattedMessage = formatLogMessage(LOG_LEVELS.DEBUG, message, context);
  console.debug(formattedMessage);
};

const logger = {
  error,
  warn,
  info,
  debug,
};

export default logger;